import PropTypes from 'prop-types';
import React__default from 'react';
import { ThumbnailsHeroVideoThumbnailSlide } from './thumbnails-hero-video-thumbnail-slide.component.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { ThumbnailsHeroBackgroundContainer } from './thumbnails-hero-background-container.component.js';
import { ThumbnailsHeroContentContainer } from './thumbnails-hero-content-container.component.js';
import { ThumbnailsHeroImagesSlidesContainer } from './thumbnails-hero-images-slides-container.component.js';
import { ThumbnailsHeroThumbnailsSlidesContainer } from './thumbnails-hero-thumbnails-slides-container.component.js';
import { Carousel } from '../carousel/carousel.component.js';
import { SCREEN_CLASS_MIN_WIDTHS } from '../../../core/constants/tokens-screen.js';
import { deprecatedPropValues } from '../core/deprecated-prop-values.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { CarouselSlide } from '../carousel/carousel-slide.component.js';
import { RenderComp } from '../core/render-comp.component.js';

const propTypes = {
    /**
   * The visual style of the thumbnails hero.
   * Available options: 'standard', 'under'
   *
   * The standard skin is deprecated - use the under skin instead
   */ skin: deprecatedPropValues(PropTypes.oneOf([
        'standard',
        'under'
    ]), [
        'standard'
    ], 'Use the `under` skin instead'),
    /**
   * The width of the content area.
   * Available options: 'standard', 'wide'
   */ contentWidth: PropTypes.oneOf([
        'standard',
        'wide'
    ]),
    /**
   * The index of the slide to select
   */ currentSlide: PropTypes.number,
    /**
   * A callback fired when the ThumbnailsHero changes the selected slide.
   * It is passed the index of the currently selected slide and the index of the slide it will change to
   */ onSlideChange: PropTypes.func,
    /**
   * Provide accessible text for slide controllers
   */ accessibleTextForDots: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
};
const propKeysToRemove = Object.keys(propTypes);
const ThumbnailsHero = renderWithRef('ThumbnailsHero', propTypes, (props, ref)=>{
    useComponentStylesLoaded('ThumbnailsHero', SWAN_STYLE_KEY_MAP.thumbnailsHero);
    const { skin = 'standard', contentWidth = 'standard', currentSlide = 0, accessibleTextForDots, onSlideChange, children } = props;
    const thumbnailsArray = [];
    const imagesArray = [];
    let backgroundContainer = null;
    let contentContainer = null;
    React__default.Children.forEach(children, (child)=>{
        if (!/*#__PURE__*/ React__default.isValidElement(child)) return;
        switch(child.type){
            case ThumbnailsHeroThumbnailsSlidesContainer:
                thumbnailsArray.push(...React__default.Children.map(child.props.children, (grandChild, index)=>[
                        CarouselSlide,
                        ThumbnailsHeroVideoThumbnailSlide
                    ].includes(grandChild.type) ? /*#__PURE__*/ React__default.cloneElement(grandChild, {
                        key: index,
                        component: grandChild.props.component || 'span'
                    }) : null));
                break;
            case ThumbnailsHeroImagesSlidesContainer:
                imagesArray.push(// eslint-disable-next-line react/no-array-index-key
                ...React__default.Children.map(child.props.children, (grandChild, index)=>grandChild.type === CarouselSlide ? /*#__PURE__*/ React__default.cloneElement(grandChild, {
                        key: index
                    }) : null));
                break;
            case ThumbnailsHeroBackgroundContainer:
                backgroundContainer = child;
                break;
            case ThumbnailsHeroContentContainer:
                contentContainer = child;
                break;
        }
    });
    const classNames = new Set([
        'swan-thumbnails-hero',
        `swan-thumbnails-hero-skin-${skin}`
    ]);
    if (contentWidth !== 'standard') classNames.add(`swan-thumbnails-hero-content-${contentWidth}`);
    return /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "div",
        forwardedRef: ref,
        propKeysToRemove: propKeysToRemove,
        classNames: classNames,
        props: props
    }, backgroundContainer, /*#__PURE__*/ React__default.createElement("div", {
        className: "swan-thumbnails-hero-images-carousel-container"
    }, /*#__PURE__*/ React__default.createElement(Carousel, {
        slidesToScroll: 1,
        slidesToShow: 1,
        arrows: false,
        accessibleTextForNext: "",
        accessibleTextForPrevious: "",
        accessibleTextForDots: accessibleTextForDots,
        customPaging: (index)=>/*#__PURE__*/ React__default.createElement("button", {
                "aria-label": accessibleTextForDots[index],
                key: index
            }, thumbnailsArray[index]),
        progressIndicator: skin === 'under' ? 'dots' : 'dots-inset',
        progressIndicatorAlignment: skin === 'under' ? 'left' : 'center',
        currentSlide: currentSlide !== null && currentSlide !== void 0 ? currentSlide : 0,
        beforeChange: onSlideChange !== null && onSlideChange !== void 0 ? onSlideChange : undefined,
        fade: true,
        responsive: [
            {
                breakpoint: SCREEN_CLASS_MIN_WIDTHS.md,
                settings: {
                    fade: false
                }
            }
        ]
    }, imagesArray)), contentContainer);
});
ThumbnailsHero.defaultProps = {
    skin: 'standard',
    contentWidth: 'standard'
};

export { ThumbnailsHero };
